import React, { useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, Theme, StyledEngineProvider } from "@mui/material/styles";
import { ThemeProvider as LegacyThemeProvider } from "@mui/styles";
import mgTheme from "assets/mgTheme";
import Home from "components/app/Home";
import { useAuth, useRefreshSession } from "components/app/auth/useAuth";
import Loading from "components/app/Loading";
import Info from "components/app/Info";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { FiltrosProvider } from "components/contexts/FiltrosContext";

import {
  useTokenPermissionsSection,
  useTokenPermission,
} from "components/hooks/usePermissionsFromToken";
import loadable from "@loadable/component";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import "react-toastify/dist/ReactToastify.css";
import "assets/toast.css";
import ToastProvider from "./ToastProvider";
import SolicitudRevision from "./consumo/Revision/SolicitudRevision";


declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}


const SignIn = loadable(() => import("./auth/SignIn"));
const SignOut = loadable(() => import("./auth/SignOut"));
const Callback = loadable(() => import("./auth/Callback"));
const Unauthorized = loadable(() => import("./auth/Unauthorized"));

const SolicitudesConsumo = loadable(
  () => import("./consumo/Solicitudes/ListaSolicitudes"),
);
const SolicitudAnalisisConsumo = loadable(
  () => import("./consumo/Solicitudes/SolicitudAnalisis"),
);
const SolicitudesRevison = loadable(
  () => import("./consumo/Revision/ListaSolicitudesAnalisis"),
);

const ReporteConsumo = loadable(
  () => import("./consumo/Reporte/ReporteDownload"),
);
const SolicitudesLiquidar = loadable(
  () => import("./consumo/Liquidar/ListaSolicitudes"),
);
const LoteLiquidacion = loadable(
  () => import("./consumo/liquidacion/ListaLoteLiquidacion"),
);

const SolicitudesAnalisis = loadable(
  () => import("./analisis/ListaSolicitudes"),
);
const SolicitudAnalisis = loadable(
  () => import("./analisis/solicitudAnalisis/SolicitudAnalisis"),
);
const VerificacionTelefonica = loadable(
  () =>
    import(
      "./analisis/solicitudAnalisis/verificacionTelefonica/VerificacionTelefonica"
    ),
);
const Reformular = loadable(
  () => import("./analisis/solicitudAnalisis/reformular/Reformular"),
);
const SolicitudesConfeccion = loadable(
  () => import("./confeccion/ListaSolicitudes"),
);
const SolicitudesLiquidacion = loadable(
  () => import("./liquidacion/ListaSolicitudes"),
);
const SolicitudConfeccion = loadable(
  () => import("./confeccion/SolicitudConfeccion"),
);
const ConfeccionDePrenda = loadable(
  () => import("./confeccion/confeccionDePrenda/ConfeccionDePrenda"),
);
const SolicitudLiquidacion = loadable(
  () => import("./liquidacion/SolicitudLiquidacion"),
);
const Liquidar = loadable(() => import("./liquidacion/liquidar/Liquidar"));
const SolicitudesAnalisisBDS = loadable(
  () => import("./auditoria/ListaSolicitudesBDS"),
);
const SolicitudBDS = loadable(() => import("./auditoria/SolicitudBDS"));
const Usuarios = loadable(() => import("./configuracion/usuarios/Usuarios"));
const ParametrosGlobales = loadable(
  () => import("./configuracion/parametrosGlobales/ParametrosGlobales"),
);
const CodigosPostales = loadable(
  () => import("./configuracion/codigosPostales/CodigosPostales"),
);
const CalificacionesPersona = loadable(
  () => import("./configuracion/calificacionesPersona/CalificacionesPersona"),
);
const Configuracion = loadable(() => import("./configuracion/Configuracion"));

const PuntosDeVentaList = loadable(
  () => import("./consumo/configuracion/puntosDeVenta/PuntosDeVentaList"),
);

const ConvenioPlan = loadable(
  () => import("./consumo/configuracion/convenioPlan/ConvenioPlan"),
);

const PlanesComercialesList = loadable(
  () => import("./consumo/configuracion/planComercial/PlanesComercialesList"),
);

const PlanComercial = loadable(
  () => import("./consumo/configuracion/planComercial/PlanComercial"),
);

const AltaPuntoDeVenta = loadable(
  () =>
    import("./consumo/configuracion/puntosDeVenta/actions/PuntoDeVentaForm"),
);

const ModificarPuntoDeVenta = loadable(
  () =>
    import(
      "./consumo/configuracion/puntosDeVenta/actions/ModificarPuntoDeVenta"
    ),
);

const DetallePuntoDeVenta = loadable(
  () => import("./consumo/configuracion/puntosDeVenta/DetallePuntoDeVenta"),
);

const CadenasList = loadable(
  () => import("./consumo/configuracion/cadenas/CadenasList"),
);

const DetalleCadena = loadable(
  () => import("./consumo/configuracion/cadenas/DetalleCadena"),
);

const DetalleSubcadena = loadable(
  () => import("./consumo/configuracion/subcadenas/DetalleSubcadena"),
);

const AltaCadena = loadable(
  () => import("./consumo/configuracion/cadenas/actions/CadenaForm"),
);

const ModificarCadena = loadable(
  () => import("./consumo/configuracion/cadenas/actions/ModificarCadena"),
);

const ModificarSubcadena = loadable(
  () =>
    import("./consumo/configuracion/subcadenas/actions/ModificarSubcadena"),
);

const LiquidarBatch = loadable(
  () => import("./consumo/liquidacion/actions/LiquidarBatch"),
);

const App = () => {
  const { isAuthenticated, isAlreadyLoggedIn, session } = useAuth();
  const ldClient = useLDClient();
  useRefreshSession();

  useEffect(() => {
    const isDataValid =
      isAuthenticated &&
      session?.user?.email &&
      session?.user?.preferred_username;

    if (isDataValid && ldClient) {
      ldClient.identify({
        kind: "user",
        key: session?.user?.email,
        email: session?.user?.email,
        name: session?.user?.preferred_username,
      });
    }
  }, [isAuthenticated, ldClient, session]);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            meta: {
              token: session?.accessToken,
            },
          },
        },
      }),
    [session?.accessToken],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider>
        <ThemeProvider theme={mgTheme}>
          <LegacyThemeProvider theme={mgTheme}>
            <FiltrosProvider>
              <CssBaseline />
              <ReactQueryDevtools />
              <Router>
                <ToastProvider />
                {isAlreadyLoggedIn &&
                  !isAuthenticated &&
                  ldClient?.getContext()?.anonymous && <Loading />}

                {!isAlreadyLoggedIn && !isAuthenticated && <PublicRoutes />}

                {isAuthenticated && !session?.user?.isAuthorized && (
                  <Unauthorized />
                )}

                {isAuthenticated &&
                  session?.user?.isAuthorized &&
                  (ldClient?.getContext() as { email?: string } | undefined)?.email && <AuthenticatedRoutes />}
              </Router>
            </FiltrosProvider>
          </LegacyThemeProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
  );
};

const PublicRoutes = () => {
  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route path="/callback" element={<Callback />} />
      <Route path="*" element={<Navigate to="/signin" replace />} />
    </Routes>
  );
};

const AuthenticatedRoutes = () => {
  const { session } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/signout" element={<SignOut />} />
      <Route path="/info" element={<Info />} />
      {session?.user?.originacion && PrendarioAuthenticatedRoutes()}
      {session?.user?.euroUserId && ConsumoAuthenticatedRoutes()}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const PrendarioAuthenticatedRoutes = () => {
  const { yenPrendario } = useFlags();

  return [
    routeWithPermissions({
      path: "/analisis",
      component: SolicitudesAnalisis,
      enabled: useTokenPermission("analisis:verListado") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/solicitudesbds",
      component: SolicitudesAnalisisBDS,
      enabled: useTokenPermission("analisis:verListado") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/solicitudesbds/:solicitudId",
      component: SolicitudBDS,
      enabled: useTokenPermission("analisis:detalle") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/analisis/:solicitudId",
      component: SolicitudAnalisis,
      enabled: useTokenPermission("analisis:detalle") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/analisis/:solicitudId/vt",
      component: VerificacionTelefonica,
      enabled: useTokenPermission("analisis:verificacionTelefonica") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/analisis/:solicitudId/reformular",
      component: Reformular,
      enabled: useTokenPermission("analisis:reformular") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/confeccion",
      component: SolicitudesConfeccion,
      enabled: useTokenPermission("confeccion:verListado") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/confeccion/:solicitudId",
      component: SolicitudConfeccion,
      enabled: useTokenPermission("confeccion:detalle") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/confeccion/:solicitudId/confeccionDePrenda",
      component: ConfeccionDePrenda,
      enabled: useTokenPermission("confeccion:confeccionarPrenda") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/liquidacion",
      component: SolicitudesLiquidacion,
      enabled: useTokenPermission("liquidacion:verListado") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/liquidacion/:solicitudId",
      component: SolicitudLiquidacion,
      enabled: useTokenPermission("liquidacion:detalle") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/liquidacion/:solicitudId/liquidar",
      component: Liquidar,
      enabled: useTokenPermission("liquidacion:liquidar") && yenPrendario,
    }),
    // <RouteWithPermissions
    //   exact
    //   path="/configuracion"
    //   component={Configuracion}
    //   enabled={yenConsumo && yenConsumoConfiguracion}
    // />,
    routeWithPermissions({
      path: "/parametros",
      component: ParametrosGlobales,
      enabled: useTokenPermission("configuracion:parametrosGlobales") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/codigospostales",
      component: CodigosPostales,
      enabled: useTokenPermission("configuracion:codigosPostales") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/calificaciones",
      component: CalificacionesPersona,
      enabled: useTokenPermission("configuracion:calificaciones") && yenPrendario,
    }),
    routeWithPermissions({
      path: "/usuarios",
      component: Usuarios,
      enabled: useTokenPermission("configuracion:usuarios") && yenPrendario,
    }),
  ];
};

const ConsumoAuthenticatedRoutes = () => {
  const {
    yenConsumo,
    yenReporteConsumo,
    yenConsumoConfiguracion,
    yenConsumoConfiguracionAbm,
    yenConsumoConfiguracionAbmEditar,
    yenConsumoAnulacionLiquidacion,
    yenConsumoConfiguracionLiquidarBatch,
  } = useFlags();
  return [
    routeWithPermissions({
      path: "/solicitudes",
      component: SolicitudesConsumo,
      enabled: yenConsumo,
    }),
    routeWithPermissions({
      path: "/solicitudes/:solicitudId",
      component: SolicitudAnalisisConsumo,
      enabled: yenConsumo,
    }),
    routeWithPermissions({
      path: "/revision",
      component: SolicitudesRevison,
      enabled: yenConsumo,
    }),
    routeWithPermissions({
      path: "/reporte",
      component: ReporteConsumo,
      enabled: yenConsumo && yenReporteConsumo,
    }),
    routeWithPermissions({
      path: "/revision/:solicitudId",
      component: SolicitudRevision,
      enabled: yenConsumo,
    }),
    routeWithPermissions({
      path: "/liquidar",
      component: SolicitudesLiquidar,
      enabled: yenConsumo,
    }),
    routeWithPermissions({
      path: "/lote-liquidacion",
      component: LoteLiquidacion,
      enabled: yenConsumo && yenConsumoAnulacionLiquidacion,
    }),
    routeWithPermissions({
      path: "/lote-liquidacion/:solicitudId",
      component: SolicitudRevision,
      enabled: yenConsumo && yenConsumoAnulacionLiquidacion,
    }),
    routeWithPermissions({
      path: "/configuracion",
      component: Configuracion,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/cadenas",
      component: CadenasList,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/cadena/nuevo",
      component: AltaCadena,
      enabled: yenConsumo && yenConsumoConfiguracion && yenConsumoConfiguracionAbm,
    }),
    routeWithPermissions({
      path: "/cadena/modificar/:cadenaId",
      component: ModificarCadena,
      enabled: yenConsumo && yenConsumoConfiguracion && yenConsumoConfiguracionAbm,
    }),
    routeWithPermissions({
      path: "/cadena/:id",
      component: DetalleCadena,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/subcadena/modificar/:subcadenaId",
      component: ModificarSubcadena,
      enabled: yenConsumo && yenConsumoConfiguracion && yenConsumoConfiguracionAbm && yenConsumoConfiguracionAbmEditar,
    }),
    routeWithPermissions({
      path: "/subcadena/:id",
      component: DetalleSubcadena,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/puntos-de-venta",
      component: PuntosDeVentaList,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/pdv/:pdvId/convenio",
      component: ConvenioPlan,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/planes-comerciales",
      component: PlanesComercialesList,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/plan/:planId",
      component: PlanComercial,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/punto-de-venta/nuevo",
      component: AltaPuntoDeVenta,
      enabled: yenConsumo && yenConsumoConfiguracion && yenConsumoConfiguracionAbm,
    }),
    routeWithPermissions({
      path: "/punto-de-venta/:pdvId",
      component: DetallePuntoDeVenta,
      enabled: yenConsumo && yenConsumoConfiguracion,
    }),
    routeWithPermissions({
      path: "/punto-de-venta/modificar/:pdvId",
      component: ModificarPuntoDeVenta,
      enabled: yenConsumo && yenConsumoConfiguracion && yenConsumoConfiguracionAbm && yenConsumoConfiguracionAbmEditar,
    }),
    routeWithPermissions({
      path: "/liquidar-batch",
      component: LiquidarBatch,
      enabled: yenConsumo && yenConsumoConfiguracion && yenConsumoConfiguracionLiquidarBatch,
    }),
  ];
};

const routeWithPermissions = ({
  path,
  component: Component,
  enabled,
}: {
  path: string;
  component: React.ComponentType<any>;
  enabled: boolean;
}) => (
  <Route
    key={path}
    path={path}
    element={enabled ? <Component /> : <Navigate to="/" replace />}
  />
);

export default App;
